/**
 * API layer for the new Azure-based backend.
 *
 * Reads are CDN-served static JSON files behind Front Door.
 * Writes go to a single HTTP-triggered Azure Function.
 *
 * Returns data in the existing IPuzzleRemoteDataV1 / IPlayerStats shapes
 * so most call sites don't need to change.
 */

import { SimpleDate } from "./SimpleDate";
import { IPuzzleRemoteDataV1 } from "./v1/interfaces/data/IPuzzleRemoteDataV1";
import { IScoringRubricV1 } from "./v1/interfaces/data/IScoringRubricV1";
import { IWordInPuzzleDataV1 } from "./v1/interfaces/data/IWordInPuzzleDataV1";
import { IPlayerStats, IPlayerStat } from "./v1/interfaces/data/IPlayerStats";
import { getClientMetadata, withClientMetadata } from "../clientMetadata";
import { storeBillingContext } from "../storeBilling";
import { recordDiagnosticEvent } from "../diagnostics";
import {
  normalizeOperationsDashboard,
  OperationsDashboardData,
  OperationsDashboardFilter,
} from "../operationsAnalytics";

export type {
  AnalyticsSurface,
  OperationsClientActivity,
  OperationsDailyActivity,
  OperationsDashboardData,
  OperationsDashboardFilter,
} from "../operationsAnalytics";

/* ============================================================
 * Configuration
 * ============================================================ */

/** Front Door CDN endpoint that fronts the public blob container. Baked in at
 *  build time (WIRDER_CDN_BASE_URL) so a build can target staging. */
const CDN_BASE_URL = __WIRDER_CDN_BASE_URL__;

/** Azure Functions endpoint for write operations. Baked in at build time
 *  (WIRDER_API_BASE_URL) so a build can target staging. */
const API_BASE_URL = __WIRDER_API_BASE_URL__;

/** Use Azurite + local Functions when this localStorage flag is set. */
const LOCAL_OVERRIDE_KEY = "WirderUseLocalhostApi";
const LOCAL_CDN_URL = "http://127.0.0.1:10000/devstoreaccount1/cdn";
const LOCAL_API_URL = "http://localhost:7071/api";
const MUTATION_TIMEOUT_MS = 20_000;

function getCdnUrl(): string {
  return localStorage[LOCAL_OVERRIDE_KEY] === "1" ? LOCAL_CDN_URL : CDN_BASE_URL;
}

function getApiUrl(): string {
  return localStorage[LOCAL_OVERRIDE_KEY] === "1" ? LOCAL_API_URL : API_BASE_URL;
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs = MUTATION_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
}

/* ============================================================
 * Public types
 * ============================================================ */

export type SubmitPlayResult =
  | { result: "Success"; regularPoints: number }
  | { result: "AlreadySubmitted" }
  | { result: "Unauthorized" }
  | { result: "PuzzleNotFound" }
  | { result: "NotTodaysPuzzle" }
  | { result: "Error"; message?: string };

export type FetchPuzzleResult =
  | { result: "Success"; puzzle: IPuzzleRemoteDataV1 }
  | { result: "NotFound" }
  | { result: "Unauthorized" }
  | { result: "Error"; message?: string };

/** Context auto-attached to in-app feedback. All fields are informational. */
export interface FeedbackContext {
  appVersion?: string;
  screen?: string;
  route?: string;
  puzzleDate?: string;
  email?: string;
  name?: string;
  platform?: string;
  userAgent?: string;
  diagnostics?: string;
}

export interface FeedbackPayload {
  sentiment: "positive" | "negative" | "feature-request";
  message: string;
  context?: FeedbackContext;
}

export type SubmitFeedbackResult =
  | { result: "Success" }
  | { result: "Unauthorized" }
  | { result: "RateLimited" }
  | { result: "Error"; message?: string };

export type FetchOperationsDashboardResult =
  | { result: "Success"; data: OperationsDashboardData }
  | { result: "Unauthorized" }
  | { result: "Error" };

export type PremiumSource = "patreon" | "complimentary" | "apple" | "google" | null;
export type PremiumTier = "bronze" | "silver" | "gold";

export interface PremiumStatus {
  billingEnvironment?: "Production" | "Sandbox";
  tier: PremiumTier | null;
  source: PremiumSource;
  subscription?: {
    store: "apple" | "google";
    environment?: "Production" | "Sandbox";
    tier: PremiumTier;
    productId: string;
    expiresAt: string;
    manageUrl: string;
  } | null;
  patreon: {
    configured: boolean;
    linked: boolean;
    tier: PremiumTier | null;
    displayName?: string;
    membershipStatus: "active_patron" | "declined_patron" | "former_patron" | "none";
  };
}

export interface PremiumProjection {
  version: 1;
  playerId: string;
  tier: PremiumTier | null;
  verifiedAt: string;
}

export type FetchPremiumStatusResult =
  | { result: "Success"; status: PremiumStatus }
  | { result: "Unauthorized" }
  | { result: "Error"; message?: string };

export type StartPatreonLinkResult =
  | { result: "Success"; authorizationUrl: string }
  | { result: "Unauthorized" }
  | { result: "Unavailable" }
  | { result: "Error"; message?: string };

export type FinalizePatreonLinkResult =
  | { result: "Success"; tier: PremiumTier | null }
  | { result: "Unauthorized" }
  | { result: "AlreadyLinked" }
  | { result: "Expired" }
  | { result: "Stale" }
  | { result: "Error"; message?: string };

/* ============================================================
 * Backend shapes (kept private to this module)
 * ============================================================ */

interface BackendPuzzleJson {
  version: number;
  puzzleId: string;
  grid: {
    rows: number;
    cols: number;
    letters: string[][];
  };
  scoringRubric: IScoringRubricV1;
  timeLimitInSeconds: number;
}

interface BackendSolutionJson {
  version: number;
  puzzleId: string;
  totalPlays: number;
  words: { word: string; foundCount: number; rating: "Gold" | "Silver" | "Bronze" | null }[];
}

interface BackendPlayJson {
  version: number;
  playerId: string;
  puzzleId: string;
  words: { word: string; path: number[][] }[];
  regularPoints: number;
  submittedAt: string;
}

interface BackendScoredPlayJson {
  version: number;
  playerId: string;
  puzzleId: string;
  words: { word: string; path: number[][]; rating: "Gold" | "Silver" | "Bronze" | null }[];
  regularPoints: number;
  rarityPoints: number;
  submittedAt: string;
}

interface BackendPlayerStatsJson {
  version: number;
  playerId: string;
  records: Record<string, { value: number; puzzleId: string; place: number } | null | undefined>;
  /** Ordered list of all puzzleIds this player has played. Added 2026-06. */
  puzzlesPlayed?: string[];
}

/* ============================================================
 * Date helpers
 * ============================================================ */

/** YYYY-MM-DD string used by the backend as puzzleId. */
export function toPuzzleId(date: SimpleDate): string {
  const m = String(date.month).padStart(2, "0");
  const d = String(date.day).padStart(2, "0");
  return `${date.year}-${m}-${d}`;
}

function fromPuzzleId(puzzleId: string): SimpleDate | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(puzzleId);
  if (!m) return undefined;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/* ============================================================
 * Low-level fetchers
 * ============================================================ */

async function fetchJson<T>(url: string): Promise<T | undefined> {
  const response = await fetch(url, { cache: "no-store" });
  if (response.status === 404) return undefined;
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching ${url}`);
  }
  return (await response.json()) as T;
}

async function fetchBackendPuzzle(
  puzzleId: string,
  cacheBust = false,
): Promise<BackendPuzzleJson | undefined> {
  // Front Door does negative caching, so a just-published puzzle can keep
  // returning a stale edge-cached 404 for a short while. When re-checking after
  // a rollover poke, append a unique query param to bypass that cached 404.
  const bust = cacheBust ? `?t=${Date.now()}` : "";
  return fetchJson<BackendPuzzleJson>(`${getCdnUrl()}/puzzles/${puzzleId}/puzzle.json${bust}`);
}

async function fetchBackendSolution(puzzleId: string): Promise<BackendSolutionJson | undefined> {
  return fetchJson<BackendSolutionJson>(`${getCdnUrl()}/puzzles/${puzzleId}/solution.json`);
}

async function fetchBackendPlay(
  puzzleId: string,
  playerId: string,
): Promise<BackendPlayJson | undefined> {
  return fetchJson<BackendPlayJson>(
    `${getCdnUrl()}/puzzles/${puzzleId}/plays/${encodeURIComponent(playerId)}.json`,
  );
}

async function fetchBackendScoredPlay(
  puzzleId: string,
  playerId: string,
): Promise<BackendScoredPlayJson | undefined> {
  return fetchJson<BackendScoredPlayJson>(
    `${getCdnUrl()}/puzzles/${puzzleId}/scoredPlays/${encodeURIComponent(playerId)}.json`,
  );
}

async function fetchBackendPlayerStats(
  playerId: string,
): Promise<BackendPlayerStatsJson | undefined> {
  return fetchJson<BackendPlayerStatsJson>(
    `${getCdnUrl()}/stats/players/${encodeURIComponent(playerId)}.json`,
  );
}

/* ============================================================
 * Mapping: backend → frontend
 * ============================================================ */

function buildPuzzleData(
  puzzle: BackendPuzzleJson,
  solution: BackendSolutionJson | undefined,
  scoredPlay: BackendScoredPlayJson | undefined,
  play: BackendPlayJson | undefined,
): IPuzzleRemoteDataV1 {
  const date = fromPuzzleId(puzzle.puzzleId) ?? { year: 0, month: 0, day: 0 };

  const wordsInPuzzle: IWordInPuzzleDataV1[] = solution
    ? solution.words.map((w) => ({ word: w.word, timesFound: w.foundCount }))
    : [];

  // Played status: scoredPlay (after rollover) is authoritative, but before
  // rollover the play exists at plays/{playerId}.json — we use that so the
  // user doesn't see "haven't played today" between submit and midnight.
  const scoredSource = scoredPlay ?? play;
  const wordsFound = scoredSource ? scoredSource.words.map((w) => w.word) : [];
  const played = scoredSource !== undefined;
  const regularPoints = scoredSource?.regularPoints ?? 0;
  const rarityPoints = scoredPlay?.rarityPoints;
  const totalPoints = scoredSource !== undefined ? regularPoints + (rarityPoints ?? 0) : undefined;

  return {
    date,
    board: puzzle.grid.letters,
    scoringRubric: puzzle.scoringRubric,
    timeLimitInSeconds: puzzle.timeLimitInSeconds,
    solution: {
      totalSubmissions: solution?.totalPlays,
      wordsInPuzzle,
    },
    wordsFound,
    played,
    regularPoints,
    rarityPoints,
    totalPoints,
    timeElapsedInSeconds: 0,
  };
}

function mapBackendRecord(
  record: { value: number; puzzleId: string; place: number } | null | undefined,
): IPlayerStat | undefined {
  if (!record) return undefined;
  const date = fromPuzzleId(record.puzzleId);
  if (!date) return undefined;
  return { value: record.value, placeOverall: record.place, date };
}

function mapBackendStats(stats: BackendPlayerStatsJson): IPlayerStats {
  const r = stats.records;

  // Build per-length records: letters[N] = record for N-letter words. The
  // backend writes one `most{N}LetterWords` key per exact length found (no
  // "10+" grouping), so we read every matching key and index it by its length.
  // The legacy `most10PlusLetterWords` key (conflated 10+ history) does not
  // match this pattern and is intentionally ignored.
  const letters: (IPlayerStat | undefined)[] = [];
  for (const key of Object.keys(r)) {
    const match = /^most(\d+)LetterWords$/.exec(key);
    if (!match) continue;
    const len = Number(match[1]);
    letters[len] = mapBackendRecord(r[key]);
  }

  return {
    // Not tracked per-player on the new backend yet.
    totalPlays: undefined,
    mostFoundWords: mapBackendRecord(r.mostWordsFound),
    totalPoints: mapBackendRecord(r.mostTotalPoints),
    regularPoints: mapBackendRecord(r.mostRegularPoints),
    rarityPoints: mapBackendRecord(r.mostRarityPoints),
    letters,
  };
}

/* ============================================================
 * Public API
 * ============================================================ */

/**
 * Fetch a puzzle by date, along with its solution + the player's scored play
 * if those are available. Returns the data in the legacy IPuzzleRemoteDataV1 shape.
 *
 * For today's puzzle, only the grid + rubric will be populated; word list and
 * scored play data only exist after the next-day rollover runs.
 */
export async function fetchPuzzleForDate(
  date: SimpleDate,
  playerId: string,
  options?: { cacheBust?: boolean },
): Promise<FetchPuzzleResult> {
  const puzzleId = toPuzzleId(date);
  try {
    const puzzle = await fetchBackendPuzzle(puzzleId, options?.cacheBust ?? false);
    if (!puzzle) return { result: "NotFound" };

    // Fetch solution + scoredPlay + play in parallel. All may be 404.
    //  - Solution exists after midnight rollover for the puzzle's date.
    //  - ScoredPlay exists after rollover, with rarity scoring.
    //  - Play exists between submission and rollover (so we can show
    //    "you've played today" before scoring happens).
    const [solution, scoredPlay, play] = await Promise.all([
      fetchBackendSolution(puzzleId),
      fetchBackendScoredPlay(puzzleId, playerId),
      fetchBackendPlay(puzzleId, playerId),
    ]);

    return { result: "Success", puzzle: buildPuzzleData(puzzle, solution, scoredPlay, play) };
  } catch (err) {
    console.error(`fetchPuzzleForDate(${puzzleId}) failed:`, err);
    return { result: "Error", message: String(err) };
  }
}

/* ============================================================
 * Rollover poke (handles "no puzzle ready yet for today")
 * ============================================================ */

/** Backoff schedule for re-checking the CDN after a poke (~30s total budget). */
const ROLLOVER_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Poke the server to run the daily rollover. Anonymous, no body — the server is
 * the authority on which date to publish and ignores anything the client sends,
 * so a client can never force a future puzzle to publish early. Best-effort:
 * the caller re-checks the CDN regardless, and multiple concurrent pokes are
 * safe (the server serializes them behind a lock), so poke failures are
 * swallowed rather than surfaced.
 */
export async function triggerRollover(): Promise<void> {
  try {
    await fetch(`${getApiUrl()}/rollover`, { method: "POST", cache: "no-store" });
  } catch (err) {
    console.error("triggerRollover poke failed:", err);
  }
}

/**
 * Handle the "today's puzzle isn't published yet" case: poke the server to run
 * the rollover, then re-check the CDN with a short bounded backoff. Cache-busts
 * each retry so a stale Front Door negative-cached 404 doesn't mask a
 * just-published puzzle. Returns the puzzle once it appears, or `NotFound` if
 * the retry budget is exhausted (caller should show a "try again shortly" UX).
 */
export async function pokeRolloverAndRefetch(
  date: SimpleDate,
  playerId: string,
): Promise<FetchPuzzleResult> {
  await triggerRollover();
  for (const delay of ROLLOVER_RETRY_DELAYS_MS) {
    await sleep(delay);
    const result = await fetchPuzzleForDate(date, playerId, { cacheBust: true });
    if (result.result !== "NotFound") return result;
  }
  return { result: "NotFound" };
}

/**
 * Submit a play for today's puzzle.
 * The caller passes a bearer token — either a session JWT minted by /api/me
 * (preferred; survives webview restarts without re-prompting the user) or a
 * fresh provider ID token (Google / Apple). The backend verifies the token
 * and derives the playerId from the verified subject. Words include the path
 * traced on the board (number[][] of [row, col] pairs).
 */
export async function submitPlay(
  bearerToken: string,
  puzzleId: string,
  words: { word: string; path: number[][] }[],
): Promise<SubmitPlayResult> {
  try {
    const client = await getClientMetadata();
    const response = await fetchWithTimeout(`${getApiUrl()}/plays`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify(withClientMetadata({ puzzleId, words }, client)),
    });

    if (response.status === 201) {
      const body = (await response.json()) as { regularPoints?: number };
      return { result: "Success", regularPoints: body.regularPoints ?? 0 };
    }
    if (response.status === 401) {
      return { result: "Unauthorized" };
    }
    if (response.status === 409) {
      return { result: "AlreadySubmitted" };
    }
    if (response.status === 404) {
      return { result: "PuzzleNotFound" };
    }
    if (response.status === 400) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      if (body.error?.includes("not today's puzzle")) {
        return { result: "NotTodaysPuzzle" };
      }
      return { result: "Error", message: body.error };
    }
    return { result: "Error", message: `HTTP ${response.status}` };
  } catch (err) {
    console.error("submitPlay failed:", err);
    return { result: "Error", message: String(err) };
  }
}

/**
 * Submit in-app feedback. The backend verifies the bearer token and files the
 * feedback as a GitHub issue. Mirrors submitPlay's auth model.
 */
export async function submitFeedback(
  bearerToken: string,
  payload: FeedbackPayload,
): Promise<SubmitFeedbackResult> {
  try {
    const response = await fetchWithTimeout(`${getApiUrl()}/feedback`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 201) {
      return { result: "Success" };
    }

    if (response.status === 401) {
      return { result: "Unauthorized" };
    }
    if (response.status === 429) {
      return { result: "RateLimited" };
    }
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: body.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("submitFeedback failed:", err);
    return { result: "Error", message: String(err) };
  }
}

export async function fetchOperationsDashboard(
  bearerToken: string,
  filter?: Partial<OperationsDashboardFilter>,
): Promise<FetchOperationsDashboardResult> {
  try {
    const query = new URLSearchParams();
    if (filter?.surface && filter.surface !== "all") {
      query.set("surface", filter.surface);
    }
    if (filter?.appVersion && filter.appVersion !== "all") {
      query.set("appVersion", filter.appVersion);
    }
    if (filter?.webVersion && filter.webVersion !== "all") {
      query.set("webVersion", filter.webVersion);
    }
    const encodedQuery = query.toString();
    const suffix = encodedQuery ? `?${encodedQuery}` : "";
    const response = await fetch(`${getApiUrl()}/ops/dashboard${suffix}`, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${bearerToken}` },
    });
    if (response.status === 401 || response.status === 403) {
      return { result: "Unauthorized" };
    }
    if (!response.ok) {
      return { result: "Error" };
    }
    const data = normalizeOperationsDashboard(await response.json());
    if (!data) {
      return { result: "Error" };
    }
    return { result: "Success", data };
  } catch {
    return { result: "Error" };
  }
}

/**
 * Fetch the player's all-time stats from CDN.
 * Returns undefined if the player has no stats yet (e.g. brand new player).
 */
export async function fetchPlayerStats(playerId: string): Promise<IPlayerStats | undefined> {
  try {
    const stats = await fetchBackendPlayerStats(playerId);
    if (!stats) return undefined;
    return mapBackendStats(stats);
  } catch (err) {
    console.error("fetchPlayerStats failed:", err);
    return undefined;
  }
}

/**
 * Fetch the list of puzzle dates this player has played (from CDN player stats).
 * Returns an empty array if stats don't exist yet or don't contain the field.
 */
export async function fetchPlayedDates(playerId: string): Promise<SimpleDate[]> {
  try {
    const stats = await fetchBackendPlayerStats(playerId);
    if (!stats?.puzzlesPlayed) return [];
    return stats.puzzlesPlayed
      .map((id) => fromPuzzleId(id))
      .filter((d): d is SimpleDate => d !== undefined);
  } catch {
    return [];
  }
}

/* ============================================================
 * Friend leaderboard — social reads (CDN) + mutations (Functions)
 * ============================================================ */

export interface PublicProfile {
  displayName: string;
  joinedAt: string;
}

export interface RosterFriend {
  playerId: string;
  displayName: string;
  since: string;
}

export interface OutgoingInvite {
  code: string;
  createdAt: string;
  expiresAt: string;
  /** The name the inviter typed for the invitee ("To"). */
  toLabel?: string;
}

export interface Roster {
  self: { playerId: string; displayName: string };
  friends: RosterFriend[];
  outgoing: OutgoingInvite[];
  updatedAt: string;
}

export type InviteStatus = "pending" | "accepted" | "cancelled";

export interface InvitePreview {
  fromDisplayName: string;
  createdAt: string;
  expiresAt: string;
  status: InviteStatus;
}

interface BackendRosterJson {
  version: number;
  self: { playerId: string; displayName: string };
  friends: RosterFriend[];
  outgoing: OutgoingInvite[];
  updatedAt: string;
}

/**
 * A single player's score for one puzzle, normalized for the leaderboard.
 * `scored` is true once the overnight rollover has produced rarity points; until
 * then only word count + regular points are known (rarity/total are undefined).
 */
export interface LeaderboardEntry {
  playerId: string;
  played: boolean;
  scored: boolean;
  wordCount: number;
  regularPoints: number;
  rarityPoints?: number;
  totalPoints?: number;
}

/**
 * Append a cache-busting query param. Roster / invite projections are MUTABLE
 * CDN blobs, so right after a mutation we must bypass any stale edge-cached copy.
 */
function bust(url: string, cacheBust?: boolean): string {
  return cacheBust ? `${url}?t=${Date.now()}` : url;
}

/** Fetch a player's roster projection by their secret rosterToken (CDN). */
export async function fetchRoster(
  rosterToken: string,
  options?: { cacheBust?: boolean },
): Promise<Roster | undefined> {
  try {
    const json = await fetchJson<BackendRosterJson>(
      bust(
        `${getCdnUrl()}/social/rosters/${encodeURIComponent(rosterToken)}.json`,
        options?.cacheBust,
      ),
    );
    if (!json) return undefined;
    return {
      self: json.self,
      friends: json.friends ?? [],
      outgoing: json.outgoing ?? [],
      updatedAt: json.updatedAt,
    };
  } catch (err) {
    console.error("fetchRoster failed:", err);
    return undefined;
  }
}

/** Fetch a player's public profile (display name) by playerId (CDN). */
export async function fetchProfile(playerId: string): Promise<PublicProfile | undefined> {
  try {
    const json = await fetchJson<{ version: number; displayName: string; joinedAt: string }>(
      `${getCdnUrl()}/social/players/${encodeURIComponent(playerId)}/profile.json`,
    );
    if (!json) return undefined;
    return { displayName: json.displayName, joinedAt: json.joinedAt };
  } catch (err) {
    console.error("fetchProfile failed:", err);
    return undefined;
  }
}

/** Fetch a player's public cosmetic tier projection from the CDN. */
export async function fetchPremiumProjection(
  playerId: string,
  now = new Date(),
): Promise<PremiumTier | null> {
  try {
    const projection = await fetchJson<PremiumProjection>(
      `${getCdnUrl()}/premium/players/${encodeURIComponent(playerId)}.json`,
    );
    if (
      projection?.version !== 1 ||
      projection.playerId !== playerId ||
      (projection.tier !== null &&
        projection.tier !== "bronze" &&
        projection.tier !== "silver" &&
        projection.tier !== "gold")
    ) {
      return null;
    }
    const verifiedAt = Date.parse(projection.verifiedAt);
    const age = now.getTime() - verifiedAt;
    return Number.isFinite(verifiedAt) && Number.isFinite(age) && age >= 0 ? projection.tier : null;
  } catch (err) {
    console.error(`fetchPremiumProjection(${playerId}) failed:`, err);
    return null;
  }
}

/** Fetch the public preview for an invite code (who invited you) from the CDN. */
export async function fetchInvitePreview(
  code: string,
  options?: { cacheBust?: boolean },
): Promise<InvitePreview | undefined> {
  try {
    const json = await fetchJson<{
      version: number;
      fromDisplayName: string;
      createdAt: string;
      expiresAt: string;
      status: InviteStatus;
    }>(bust(`${getCdnUrl()}/social/invites/${encodeURIComponent(code)}.json`, options?.cacheBust));
    if (!json) return undefined;
    return {
      fromDisplayName: json.fromDisplayName,
      createdAt: json.createdAt,
      expiresAt: json.expiresAt,
      status: json.status,
    };
  } catch (err) {
    console.error("fetchInvitePreview failed:", err);
    return undefined;
  }
}

/**
 * Fetch one player's score for a given puzzle for the leaderboard. Reads the
 * same public score files as the puzzle screen: the scoredPlay (post-rollover,
 * with rarity) is authoritative; before rollover we fall back to the raw play
 * so today's word count + regular points still show. All 404 = not played.
 */
export async function fetchLeaderboardEntry(
  puzzleId: string,
  playerId: string,
): Promise<LeaderboardEntry> {
  const notPlayed: LeaderboardEntry = {
    playerId,
    played: false,
    scored: false,
    wordCount: 0,
    regularPoints: 0,
  };
  try {
    const [scoredPlay, play] = await Promise.all([
      fetchBackendScoredPlay(puzzleId, playerId),
      fetchBackendPlay(puzzleId, playerId),
    ]);
    if (scoredPlay) {
      return {
        playerId,
        played: true,
        scored: true,
        wordCount: scoredPlay.words.length,
        regularPoints: scoredPlay.regularPoints,
        rarityPoints: scoredPlay.rarityPoints,
        totalPoints: scoredPlay.regularPoints + scoredPlay.rarityPoints,
      };
    }
    if (play) {
      return {
        playerId,
        played: true,
        scored: false,
        wordCount: play.words.length,
        regularPoints: play.regularPoints,
      };
    }
    return notPlayed;
  } catch (err) {
    console.error(`fetchLeaderboardEntry(${puzzleId}, ${playerId}) failed:`, err);
    return notPlayed;
  }
}

/** A word a player found, with its rare-word rating (null until scored). */
export interface FoundWord {
  word: string;
  rating: "Gold" | "Silver" | "Bronze" | null;
}

/**
 * One player's full play for a puzzle, for the "see a friend's words" view: the
 * board (so their words can be spotted on it) plus the words they found with
 * ratings and their score. Reads the same public CDN files as the leaderboard.
 */
export interface PlayerPlay {
  board: string[][];
  played: boolean;
  scored: boolean;
  words: FoundWord[];
  regularPoints: number;
  rarityPoints?: number;
  totalPoints?: number;
}

export type FetchPlayerPlayResult =
  | { result: "Success"; play: PlayerPlay }
  | { result: "NotFound" }
  | { result: "Error"; message?: string };

/**
 * Fetch a single player's play for a puzzle date so it can be reviewed on the
 * leaderboard. Loads the puzzle grid (for the board) alongside that player's
 * scoredPlay (post-rollover, with rarity ratings) or raw play (pre-rollover).
 * `NotFound` means the puzzle itself isn't published; a published puzzle with
 * no play for the player returns Success with `played: false`.
 */
export async function fetchPlayerPlay(
  date: SimpleDate,
  playerId: string,
): Promise<FetchPlayerPlayResult> {
  const puzzleId = toPuzzleId(date);
  try {
    const puzzle = await fetchBackendPuzzle(puzzleId);
    if (!puzzle) return { result: "NotFound" };

    const [scoredPlay, play] = await Promise.all([
      fetchBackendScoredPlay(puzzleId, playerId),
      fetchBackendPlay(puzzleId, playerId),
    ]);

    const board = puzzle.grid.letters;
    if (scoredPlay) {
      return {
        result: "Success",
        play: {
          board,
          played: true,
          scored: true,
          words: scoredPlay.words.map((w) => ({ word: w.word, rating: w.rating })),
          regularPoints: scoredPlay.regularPoints,
          rarityPoints: scoredPlay.rarityPoints,
          totalPoints: scoredPlay.regularPoints + scoredPlay.rarityPoints,
        },
      };
    }
    if (play) {
      return {
        result: "Success",
        play: {
          board,
          played: true,
          scored: false,
          words: play.words.map((w) => ({ word: w.word, rating: null })),
          regularPoints: play.regularPoints,
        },
      };
    }
    return {
      result: "Success",
      play: {
        board,
        played: false,
        scored: false,
        words: [],
        regularPoints: 0,
      },
    };
  } catch (err) {
    console.error(`fetchPlayerPlay(${puzzleId}, ${playerId}) failed:`, err);
    return { result: "Error", message: String(err) };
  }
}

/* ---- Mutations (authenticated Azure Functions) ---- */

export type CreateInviteResult =
  | { result: "Success"; code: string; expiresAt: string }
  | { result: "Unauthorized" }
  | { result: "RateLimited" }
  | { result: "Error"; message?: string };

export type AcceptInviteResult =
  | { result: "Success"; friend: { playerId: string; displayName: string } }
  | { result: "NotFound" }
  | { result: "Expired" }
  | { result: "AlreadyUsed" }
  | { result: "Invalid" }
  | { result: "Unauthorized" }
  | { result: "Error"; message?: string };

export type SocialActionResult =
  | { result: "Success" }
  | { result: "Unauthorized" }
  | { result: "Error"; message?: string };

async function postJson(
  path: string,
  bearerToken: string,
  body: unknown,
  method: "POST" | "PUT" = "POST",
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(`${getApiUrl()}${path}`, {
    method,
    signal,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${bearerToken}`,
    },
    body: JSON.stringify(body),
  });
}

/** Create a single-use invite link. Returns the opaque code + expiry.
 *  `from` is the invitee-facing name for the inviter (also saved as the
 *  inviter's display-name default); `to` is the inviter's private nickname for
 *  the invitee. */
export async function createInvite(
  bearerToken: string,
  from: string,
  to: string,
): Promise<CreateInviteResult> {
  const controller = new AbortController();
  // Keep the deadline active through response-body consumption too.
  const timeout = window.setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
  try {
    const response = await postJson(
      "/friends/invite",
      bearerToken,
      { from, to },
      "POST",
      controller.signal,
    );
    if (response.status === 201) {
      const body = (await response.json()) as { code: string; expiresAt: string };
      return { result: "Success", code: body.code, expiresAt: body.expiresAt };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    if (response.status === 429) return { result: "RateLimited" };
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: body.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("createInvite failed:", err);
    return { result: "Error", message: String(err) };
  } finally {
    window.clearTimeout(timeout);
  }
}

/** Accept an invite by code, forming a mutual friendship. `name` is the
 *  accepter's chosen name for the inviter on their own leaderboard. */
export async function acceptInvite(
  bearerToken: string,
  code: string,
  name?: string,
): Promise<AcceptInviteResult> {
  try {
    const response = await postJson("/friends/accept", bearerToken, { code, name });
    if (response.status === 200) {
      const body = (await response.json()) as {
        friend: { playerId: string; displayName: string };
      };
      return { result: "Success", friend: body.friend };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    if (response.status === 404) return { result: "NotFound" };
    if (response.status === 410) return { result: "Expired" };
    if (response.status === 409) return { result: "AlreadyUsed" };
    if (response.status === 400) return { result: "Invalid" };
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: body.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("acceptInvite failed:", err);
    return { result: "Error", message: String(err) };
  }
}

/** Inviter revokes a pending invite. */
export async function cancelInvite(bearerToken: string, code: string): Promise<SocialActionResult> {
  return simpleAction("/friends/cancel", bearerToken, { code });
}

/** Unfriend a player (removes both edges). */
export async function removeFriend(
  bearerToken: string,
  friendId: string,
): Promise<SocialActionResult> {
  return simpleAction("/friends/remove", bearerToken, { friendId });
}

/** Rename a friend (changes only the caller's private label for them). */
export async function renameFriend(
  bearerToken: string,
  friendId: string,
  name: string,
): Promise<SocialActionResult> {
  return simpleAction("/friends/rename", bearerToken, { friendId, name });
}

/** Set the signed-in player's display name. */
export async function setDisplayName(
  bearerToken: string,
  displayName: string,
): Promise<
  | { result: "Success"; displayName: string }
  | { result: "Unauthorized" }
  | { result: "Error"; message?: string }
> {
  try {
    const response = await postJson("/profile", bearerToken, { displayName }, "PUT");
    if (response.status === 200) {
      const body: unknown = await response.json();
      if (
        !body ||
        typeof body !== "object" ||
        !("displayName" in body) ||
        typeof body.displayName !== "string" ||
        !body.displayName.trim()
      ) {
        throw new Error("Invalid display name response");
      }
      return { result: "Success", displayName: body.displayName };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: body.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("setDisplayName failed:", err);
    return { result: "Error", message: String(err) };
  }
}

export type SelfSocialResult =
  | { result: "Success"; playerId: string; displayName: string; rosterToken: string }
  | { result: "Unauthorized" }
  | { result: "Error"; message: string };

/**
 * Fetch the signed-in player's own social identity (playerId, displayName, and
 * the secret rosterToken that gates their CDN roster). Used on boot to recover
 * the rosterToken for players who signed in before the friend leaderboard
 * shipped, or who are on a second device — the token is otherwise only handed
 * out by /api/me during interactive sign-in.
 */
export async function fetchSelfSocial(bearerToken: string): Promise<SelfSocialResult> {
  try {
    const response = await postJson("/friends/self", bearerToken, {});
    if (response.status === 200) {
      const body = (await response.json()) as {
        playerId: string;
        displayName: string;
        rosterToken: string;
      };
      return {
        result: "Success",
        playerId: body.playerId,
        displayName: body.displayName,
        rosterToken: body.rosterToken,
      };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    const parsed = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: parsed.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("fetchSelfSocial failed:", err);
    return { result: "Error", message: String(err) };
  }
}

/** Refresh the signed-in player's premium entitlement. */
export async function fetchPremiumStatus(bearerToken: string): Promise<FetchPremiumStatusResult> {
  const response = await billingRequest<PremiumStatus>(bearerToken, "status", {});
  return response.result === "Success"
    ? { result: "Success", status: response.data }
    : response;
}

async function billingRequest<T>(
  bearerToken: string,
  action: "status" | "catalog" | "verify" | "restore" | "reset-sandbox",
  body: Record<string, unknown>,
): Promise<
  { result: "Success"; data: T } | { result: "Unauthorized" } | { result: "Error"; message: string }
> {
  const failure = (
    stage: "app-verification" | "network" | "response" | "service",
    message: string,
    status?: number,
  ): { result: "Error"; message: string } => {
    console.error(`Subscription ${action} failed at ${stage}`);
    recordDiagnosticEvent("subscription-request", { action, stage, status, outcome: "failed" });
    return { result: "Error", message };
  };
  let context: Awaited<ReturnType<typeof storeBillingContext>>;
  try {
    context = await storeBillingContext();
  } catch {
    return failure(
      "app-verification",
      "The App Store could not verify this app for subscriptions. Please try again.",
    );
  }
  let response: Response;
  try {
    response = await postJson(`/premium/${action === "status" ? "status" : `store/${action}`}`,
      bearerToken, { ...body, ...context });
  } catch {
    return failure("network", "Could not reach the subscription service. Please try again.");
  }
  if (response.status === 401) return { result: "Unauthorized" };
  let data: T & { error?: string };
  try {
    data = await response.json();
  } catch {
    return failure("response", "Could not read the subscription service response. Please try again.",
      response.status);
  }
  if (!response.ok) {
    return failure("service", typeof data?.error === "string" ? data.error :
      "Subscription service is unavailable. Please try again.", response.status);
  }
  return { result: "Success", data };
}

/** Perform an authenticated store operation using the current native app evidence. */
export async function storeBillingRequest<T>(
  bearerToken: string,
  action: "catalog" | "verify" | "restore" | "reset-sandbox",
  body: Record<string, unknown>,
): Promise<
  { result: "Success"; data: T } | { result: "Unauthorized" } | { result: "Error"; message: string }
> {
  return billingRequest<T>(bearerToken, action, body);
}

/** Begin the server-side Patreon OAuth authorization-code flow. */
export async function startPatreonLink(
  bearerToken: string,
  transactionProof: string,
  returnUrl: string,
): Promise<StartPatreonLinkResult> {
  try {
    const response = await postJson("/premium/patreon/start", bearerToken, {
      transactionProof,
      returnUrl,
    });
    if (response.status === 200) {
      const body = (await response.json()) as { authorizationUrl: string };
      return { result: "Success", authorizationUrl: body.authorizationUrl };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    if (response.status === 503) return { result: "Unavailable" };
    const parsed = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: parsed.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error("startPatreonLink failed:", err);
    return { result: "Error", message: String(err) };
  }
}

/** Complete a staged Patreon callback result for the authenticated player. */
export async function finalizePatreonLink(
  bearerToken: string,
  resultId: string,
  transactionProof: string,
): Promise<FinalizePatreonLinkResult> {
  try {
    const response = await postJson("/premium/patreon/finalize", bearerToken, {
      resultId,
      transactionProof,
    });
    if (response.status === 200) {
      const body = (await response.json()) as {
        tier: PremiumTier | null;
      };
      return {
        result: "Success",
        tier: body.tier,
      };
    }
    if (response.status === 401) return { result: "Unauthorized" };
    if (response.status === 410) return { result: "Expired" };
    if (response.status === 409) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      return body.error?.includes("already linked")
        ? { result: "AlreadyLinked" }
        : { result: "Stale" };
    }
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
    };
    return {
      result: "Error",
      message: body.error ?? `HTTP ${response.status}`,
    };
  } catch (err) {
    return { result: "Error", message: String(err) };
  }
}

/** Remove the signed-in player's stored Patreon OAuth linkage. */
export async function unlinkPatreon(bearerToken: string): Promise<SocialActionResult> {
  return simpleAction("/premium/patreon/unlink", bearerToken, {});
}

async function simpleAction(
  path: string,
  bearerToken: string,
  body: unknown,
  method: "POST" | "PUT" = "POST",
): Promise<SocialActionResult> {
  try {
    const response = await postJson(path, bearerToken, body, method);
    if (response.status === 200) return { result: "Success" };
    if (response.status === 401) return { result: "Unauthorized" };
    const parsed = (await response.json().catch(() => ({}))) as { error?: string };
    return { result: "Error", message: parsed.error ?? `HTTP ${response.status}` };
  } catch (err) {
    console.error(`${path} failed:`, err);
    return { result: "Error", message: String(err) };
  }
}
